import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth } from '@common/auth/models/auth.model';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { User } from '@common/users/models/user.model';
import { AddPasswordRequest } from '@portal-core/auth/models/add-password-request.model';
import { AuthLicenseUserData } from '@portal-core/auth/models/auth-license-user-data.model';
import { CentralInstance } from '@portal-core/auth/models/central-instance.model';
import { CompletedInvite } from '@portal-core/auth/models/completed-invite.model';
import { HandoffToken } from '@portal-core/auth/models/handoff-token.model';
import { SubdomainLicense } from '@portal-core/auth/models/subdomain-license.model';
import { VerifiedUser } from '@portal-core/auth/models/verified-user.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { AuthInterceptorHttpParams } from '@portal-core/auth/util/auth-interceptor-http-params';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthApiService {
  constructor(protected http: HttpClient, protected errorService: ErrorService, protected apiService: ApiService) { }

  @SharePendingRequest()
  getUser$(ignoreAuthErrors: boolean = false): Observable<User> {
    return this.http.get<User>(`${this.apiService.centralApiBaseUri}/api/users/self`, {
      params: new AuthInterceptorHttpParams({
        ignoreAuthErrors
      })
    });
  }

  @SharePendingRequest()
  getLicenseUserData$(ignoreAuthErrors: boolean = false): Observable<AuthLicenseUserData> {
    return this.http.get<AuthLicenseUserData>(`${this.apiService.centralApiBaseUri}/api/Users/MyLicenseData`, {
      params: new AuthInterceptorHttpParams({
        ignoreAuthErrors
      })
    });
  }

  @SharePendingRequest()
  login$(email: string, password: string): Observable<Auth> {
    const body = new URLSearchParams();
    body.set('client_id', environment.centralApiClientId);
    body.set('grant_type', 'password');
    body.set('username', email);
    body.set('password', password);

    return this.http.post<Auth>(`${this.apiService.centralApiBaseUri}/token`, body.toString(), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      params: new AuthInterceptorHttpParams({
        ignoreRefreshToken: true
      })
    });
  }

  @SharePendingRequest()
  refreshToken$(): Observable<void> {
    // The server reads the refresh token from its HttpOnly cookie and takes the client from the token's session
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');

    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/token/refresh`, body.toString(), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      params: new AuthInterceptorHttpParams({
        ignoreRefreshToken: true
      })
    });
  }

  logout$(returnUrl: string): Observable<any> {
    return this.http.post(`${this.apiService.centralApiBaseUri}/api/users/logout${returnUrl ? '?returnUrl=' + encodeURIComponent(returnUrl) : ''}`, {}, {
      responseType: 'text',
      withCredentials: true
    });
  }

  loginToLicense$(): Observable<Auth> {
    return this.http.post<Auth>(`${this.apiService.centralApiBaseUri}/api/licenses/login`, { withCredentials: true });
  }

  requestHandoffToken$(licenseId: number): Observable<HandoffToken> {
    return this.http.post<HandoffToken>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/token/handoff?clientId=${encodeURIComponent(environment.centralApiClientId)}`, {
      withCredentials: true
    });
  }

  redeemHandoffToken$(token: string): Observable<any> {
    return this.http.post(`${this.apiService.centralApiBaseUri}/token/handoff/redeem`, {
      ClientId: environment.centralApiClientId,
      Token: token
    });
  }

  /**
   * Requests a handoff token that moves the session to the portal host of the license the user is
   * already signed in to. Unlike requestHandoffToken$ this never crosses a license boundary, so it
   * works for SSO licenses too.
   */
  requestRelocationHandoffToken$(): Observable<HandoffToken> {
    return this.http.post<HandoffToken>(`${this.apiService.centralApiBaseUri}/api/licenses/token/handoff/relocate`, {}, {
      // A caller who is not signed in gets a 401 here, which is an answer rather than an error:
      // it means the login form should be shown instead.
      params: new AuthInterceptorHttpParams({ ignoreAuthErrors: true }, { clientId: environment.centralApiClientId })
    });
  }

  /**
   * Redeems a handoff token against `origin` rather than the API host, so the session cookies are
   * set on that origin.
   */
  redeemHandoffTokenAt$(origin: string, token: string): Observable<any> {
    return this.http.post(`${origin}/token/handoff/redeem`, {
      ClientId: environment.centralApiClientId,
      Token: token
    }, {
      params: new AuthInterceptorHttpParams({ ignoreAuthErrors: true, ignoreRefreshToken: true })
    });
  }

  addPassword$(addPasswordRequest: AddPasswordRequest): Observable<MadCloudResult> {
    return this.http.post<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/users/AddPassword`, addPasswordRequest);
  }

  changeExpiredPassword$(userName: string, currentPassword: string, newPassword: string): Observable<any> {
    return this.http.put(`${this.apiService.centralApiBaseUri}/api/users/ChangeExpiredPassword`, {
      CurrentPassword: currentPassword,
      NewPassword: newPassword,
      UserName: userName
    });
  }

  @SharePendingRequest()
  completeInvite$(userId: string): Observable<CompletedInvite> {
    return this.http.get<CompletedInvite>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/UserToCompleteRegistration`);
  }

  requestPasswordReset$(email: string, client?: string): Observable<MadCloudResult> {
    return this.http.post<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/users/ForgotPassword`, null, { params: { email, client } });
  }

  requestSiteAccess$(): Observable<MadCloudResult> {
    return this.http.post<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/users/RequestSiteAccess`, null);
  }

  requestLicenseAccess$(): Observable<MadCloudResult> {
    return this.http.post<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/users/RequestLicenseAccess`, null);
  }

  samlSsoLogin$(returnUrl: string, clientDomain: string): Observable<any> {
    const body = new URLSearchParams();
    body.set('client_id', environment.centralApiClientId);
    body.set('return_url', returnUrl ? returnUrl : '');
    body.set('client_domain', clientDomain);

    return this.http.post(`${this.apiService.centralApiBaseUri}/api/users/SamlLoginRequest`, body.toString(), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      responseType: 'text'
    });
  }

  resetPassword$(userId: string, token: string, password: string): Observable<MadCloudResult> {
    return this.http.post<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/users/ResetPassword`, {
      NewPassword: password,
      Token: token,
      UserId: userId
    });
  }

  updatePassword$(userId: string, currentPassword: string, newPassword: string): Observable<MadCloudResult> {
    return this.http.put<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/ChangePassword`, {
      CurrentPassword: currentPassword,
      NewPassword: newPassword
    });
  }

  verifyUserToken$(userId: string, token: string, confirmEmail: boolean): Observable<VerifiedUser> {
    return this.http.post<VerifiedUser>(`${this.apiService.centralApiBaseUri}/api/users/VerifyUserToken`, {
      ConfirmEmail: confirmEmail,
      Token: token,
      UserId: userId
    });
  }

  verifySsoUserToken$(userId: string, token: string, teamName?: string): Observable<MadCloudResult> {
    return this.http.post<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/ConfirmUserTokenAndCreate${teamName ? '?teamName=' + encodeURIComponent(teamName) : ''}`, JSON.stringify(token),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      });
  }

  @SharePendingRequest()
  getMinPasswordLength$(userName: string): Observable<number> {
    return this.http.get<number>(`${this.apiService.centralApiBaseUri}/api/users/${userName}/GetMinPasswordLength`);
  }

  @SharePendingRequest()
  getCentralInstances$(): Observable<CentralInstance[]> {
    return this.http.get<CentralInstance[]>(`${this.apiService.centralGlobalApiBaseUri}/api/common/instances`);
  }

  @SharePendingRequest()
  getLicenseCentralInstance$(licenseVanityUrl: string): Observable<CentralInstance> {
    return this.http.get<CentralInstance>(`${this.apiService.centralGlobalApiBaseUri}/api/licenses/${licenseVanityUrl}/instance`);
  }

  @SharePendingRequest()
  getCentralInstanceByInstanceCode$(instanceCode: string): Observable<CentralInstance> {
    return this.http.get<CentralInstance>(`${this.apiService.centralGlobalApiBaseUri}/api/common/instance/${instanceCode}`);
  }

  @SharePendingRequest()
  getIsVanityUrlPresent$(vanity: string, checkAllInstances: boolean): Observable<MadCloudResult> {
    return this.http.post<MadCloudResult>(`${this.apiService.centralGlobalApiBaseUri}/api/licenses/IsVanityUrlPresent?checkAllInstances=${checkAllInstances}`, { VanityBaseUrl: vanity });
  }

  @SharePendingRequest()
  getLicenseByDomain$(): Observable<SubdomainLicense> {
    return this.http.get<SubdomainLicense>(`${this.apiService.centralApiBaseUri}/api/licenses/getDomainLicense`);
  }
}
